import { Alert, AlertDescription, Badge, Button, Skeleton, Textarea, toast } from "@veritrellis/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";
import { useAdminPageMeta } from "../hooks/useAdminPageMeta.js";
import { Link, Navigate, useParams } from "react-router-dom";
import { CopyButton } from "../components/CopyButton.js";
import { apiFetch } from "../lib/api.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";
import {
  type Actor,
  actorBadgeVariant,
  envLabel,
  formatActorLabel,
  formatDateTime,
  formatRequestStatus,
  statusBadgeVariant
} from "../lib/requestPresentation.js";

type Environment = "sandbox" | "production";

type RequestDetail = {
  request: {
    id: string;
    environment: Environment;
    action_type: string;
    status: string;
    reason_code: string | null;
    payload: unknown;
    resource_ref: string | null;
    created_at: string;
    expires_at: string;
  };
  permit: { jwt: string; expires_at: string } | null;
  approval: {
    decision: string;
    comment: string | null;
    created_at: string;
    decided_by: Actor | null;
  } | null;
  audit: Array<{
    id: string;
    event_type: string;
    created_at: string;
    data: unknown;
    actor: Actor;
  }>;
};

const formatEventType = (eventType: string) =>
  eventType
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const ApproverInlineActions = (input: {
  workspaceId: string;
  requestId: string;
  environment: Environment;
}) => {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");

  const decide = useMutation({
    mutationFn: async (decision: "approve" | "reject") => {
      const res = await apiFetch(
        `/admin/workspaces/${input.workspaceId}/requests/${input.requestId}/${decision}`,
        {
          method: "POST",
          body: JSON.stringify({
            environment: input.environment,
            comment: comment.length > 0 ? comment : undefined
          })
        }
      );
      if (!res.ok) {
        throw new Error(`decision_${String(res.status)}`);
      }
      return decision;
    },
    onSuccess: (decision) => {
      toast.show({
        title: decision === "approve" ? "Approved" : "Rejected",
        message: "Decision recorded.",
        color: decision === "approve" ? "green" : "red"
      });
      void queryClient.invalidateQueries({
        queryKey: ["request", input.workspaceId, input.requestId]
      });
      void queryClient.invalidateQueries({ queryKey: ["approvals", input.workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ["requests", input.workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ["summary", input.workspaceId] });
    },
    onError: (err: Error) => {
      const msg = err.message === "decision_409" ? "No longer pending" : "Decision failed";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  return (
    <div className="admin-decision-card">
      <div className="admin-decision-card-body">
        <p className="admin-decision-card-title">This request is waiting for your decision</p>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
          placeholder="Comment (recommended when rejecting)"
          rows={2}
        />
        <div className="admin-decision-actions">
          <Button
            variant="outline"
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate("reject")}
          >
            Reject
          </Button>
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate("approve")}>
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
};

export const RequestDetailPage = () => {
  const { id } = useParams();
  const { workspaceId, isLoading } = useWorkspaceId();
  const { role } = useCurrentRole();

  const q = useQuery({
    queryKey: ["request", workspaceId, id],
    enabled: workspaceId !== null && typeof id === "string",
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/requests/${id as string}`);
      if (!res.ok) {
        throw new Error("request_failed");
      }
      return (await res.json()) as RequestDetail;
    }
  });

  const pageMeta = useMemo(() => {
    if (q.data === undefined) {
      return null;
    }
    const req = q.data.request;
    return {
      title: req.action_type,
      description: `${formatRequestStatus(req.status)} · ${envLabel(req.environment)}`,
      environmentScoped: false
    };
  }, [q.data]);

  useAdminPageMeta(pageMeta);

  if (isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 24, width: 120 }} />
        <Skeleton style={{ height: 48, width: "60%" }} />
        <Skeleton style={{ height: 400, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (typeof id !== "string") {
    return (
      <div className="admin-panel-body-padded">
        <p style={{ margin: 0, fontSize: 14 }}>Invalid request id.</p>
      </div>
    );
  }
  if (q.isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 24, width: 120 }} />
        <Skeleton style={{ height: 48, width: "60%" }} />
        <Skeleton style={{ height: 400, width: "100%" }} />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="admin-page-stack">
        <Link to="/requests" className="admin-breadcrumb">
          <ArrowLeft style={{ width: 14, height: 14 }} />
          Requests
        </Link>
        <div className="admin-panel">
          <div className="admin-panel-body-padded">
            <p style={{ margin: 0, fontSize: 14, color: "var(--destructive)" }}>
              Request not found or you do not have access.
            </p>
            <Button asChild variant="secondary" size="sm" style={{ marginTop: 12 }}>
              <Link to="/requests">Back to requests</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const data = q.data;
  if (data === undefined) {
    return null;
  }

  const canAct = roleAtLeast(role, "approver");
  const isPending = data.request.status === "pending_approval";
  const req = data.request;

  return (
    <div className="admin-page-stack">
      <Link to="/requests" className="admin-breadcrumb">
        <ArrowLeft style={{ width: 14, height: 14 }} />
        Requests
      </Link>

      <header className="admin-detail-hero">
        <div className="admin-detail-title-row">
          <Badge variant={statusBadgeVariant(req.status)}>{formatRequestStatus(req.status)}</Badge>
          <Badge variant="outline">{envLabel(req.environment)}</Badge>
        </div>
        <div className="admin-detail-id-row">
          <span>{req.id}</span>
          <CopyButton value={req.id} label="Copy request ID" />
        </div>
      </header>

      {isPending && canAct ? (
        <ApproverInlineActions
          workspaceId={workspaceId}
          requestId={id}
          environment={req.environment}
        />
      ) : null}
      {isPending && !canAct ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription>
            Pending approval. Your role ({role ?? "unknown"}) cannot act on this request.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="admin-detail-grid">
        <div className="admin-detail-main">
          <section className="admin-panel" aria-labelledby="audit-timeline-heading">
            <div className="admin-panel-header">
              <h2 id="audit-timeline-heading" className="admin-panel-title">
                Audit timeline
              </h2>
              <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                {data.audit.length} events
              </span>
            </div>
            <div className="admin-panel-body">
              {data.audit.length === 0 ? (
                <div className="admin-panel-body-padded">
                  <p style={{ margin: 0, fontSize: 14, color: "var(--muted-foreground)" }}>
                    No audit events recorded yet.
                  </p>
                </div>
              ) : (
                <div className="admin-timeline" role="list">
                  {data.audit.map((e) => (
                    <article key={e.id} className="admin-timeline-item" role="listitem">
                      <div className="admin-timeline-marker" aria-hidden>
                        <span className="admin-timeline-dot" />
                      </div>
                      <div className="admin-timeline-content">
                        <div className="admin-timeline-header">
                          <p className="admin-timeline-event">{formatEventType(e.event_type)}</p>
                          <time className="admin-timeline-time" dateTime={e.created_at}>
                            {formatDateTime(e.created_at)}
                          </time>
                        </div>
                        <div className="admin-timeline-meta">
                          <Badge variant={actorBadgeVariant(e.actor.type)} className="normal-case">
                            {formatActorLabel(e.actor)}
                          </Badge>
                        </div>
                        {e.data !== null && e.data !== undefined ? (
                          <pre className="admin-code-block" style={{ marginTop: 10 }}>
                            {JSON.stringify(e.data, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="admin-panel" aria-labelledby="payload-heading">
            <div className="admin-panel-header">
              <h2 id="payload-heading" className="admin-panel-title">
                Payload
              </h2>
            </div>
            <div className="admin-panel-body-padded">
              <pre className="admin-code-block">{JSON.stringify(req.payload, null, 2)}</pre>
            </div>
          </section>

          {data.permit !== null ? (
            <section className="admin-panel" aria-labelledby="permit-heading">
              <div className="admin-panel-header">
                <h2 id="permit-heading" className="admin-panel-title">
                  Permit JWT
                </h2>
                <CopyButton value={data.permit.jwt} label="Copy permit JWT" />
              </div>
              <div className="admin-panel-body-padded">
                <p className="admin-meta-label" style={{ marginBottom: 8 }}>
                  Expires {formatDateTime(data.permit.expires_at)}
                </p>
                <pre className="admin-code-block">{data.permit.jwt}</pre>
              </div>
            </section>
          ) : null}

          {data.approval !== null ? (
            <section className="admin-panel" aria-labelledby="approval-heading">
              <div className="admin-panel-header">
                <h2 id="approval-heading" className="admin-panel-title">
                  Human decision
                </h2>
              </div>
              <div className="admin-panel-body-padded">
                <div className="admin-timeline-meta" style={{ marginTop: 0 }}>
                  <Badge
                    variant={data.approval.decision === "approved" ? "success" : "destructive"}
                  >
                    {data.approval.decision}
                  </Badge>
                  {data.approval.decided_by !== null ? (
                    <Badge variant={actorBadgeVariant(data.approval.decided_by.type)}>
                      {formatActorLabel(data.approval.decided_by)}
                    </Badge>
                  ) : null}
                  <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                    {formatDateTime(data.approval.created_at)}
                  </span>
                </div>
                {data.approval.comment !== null ? (
                  <p style={{ margin: "12px 0 0", fontSize: 14, lineHeight: 1.5 }}>
                    {data.approval.comment}
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="admin-detail-sidebar">
          <section className="admin-panel" aria-labelledby="request-meta-heading">
            <div className="admin-panel-header">
              <h2 id="request-meta-heading" className="admin-panel-title">
                Details
              </h2>
            </div>
            <div className="admin-panel-body-padded">
              <dl className="admin-meta-grid">
                <div>
                  <dt className="admin-meta-label">Created</dt>
                  <dd className="admin-meta-value">{formatDateTime(req.created_at)}</dd>
                </div>
                <div>
                  <dt className="admin-meta-label">Expires</dt>
                  <dd className="admin-meta-value">{formatDateTime(req.expires_at)}</dd>
                </div>
                {req.resource_ref !== null ? (
                  <div>
                    <dt className="admin-meta-label">Resource</dt>
                    <dd className="admin-meta-value admin-meta-value-mono">{req.resource_ref}</dd>
                  </div>
                ) : null}
                {req.reason_code !== null ? (
                  <div>
                    <dt className="admin-meta-label">Reason code</dt>
                    <dd className="admin-meta-value">{req.reason_code}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="admin-meta-label">Environment</dt>
                  <dd className="admin-meta-value">{envLabel(req.environment)}</dd>
                </div>
                <div>
                  <dt className="admin-meta-label">Status</dt>
                  <dd className="admin-meta-value">
                    <Badge variant={statusBadgeVariant(req.status)}>
                      {formatRequestStatus(req.status)}
                    </Badge>
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};
